import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, MessageSquare, Clock, User } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setFormData({ name: '', phone: '', email: '', service: '', message: '' });
  };

  const whatsappNumber = '919947222426';
  const whatsappMessage = 'Hello! I am interested in your interior design services.';
  const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(whatsappMessage)}`;

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: 'Office Address',
      details: [
        'JMJ Interior & Furnitures',
        'Kurumkanny RPS Buildings',
        'Panacheppally P.O, Kanjirappally'
      ]
    },
    {
      icon: <MapPin size={24} />,
      title: 'Showroom Address',
      details: [
        'Holy Family Forane Church Building',
        'Near Akshaya Centre',
        'NH 220, Ponkunnam'
      ]
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      details: ['+91 9947222426']
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      details: ['jmjinteriorkerala@gmail.com']
    },
    {
      icon: <Clock size={24} />,
      title: 'Working Hours',
      details: [
        'Monday - Saturday: 9:00 AM - 6:00 PM',
        'Sunday: By Appointment'
      ]
    }
  ];

  const services = [
    'Interior Design',
    'Custom Furniture',
    'Modular Kitchen',
    'Commercial Interiors',
    'Design Consultation',
    'Renovation'
  ];

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="contact-hero-content"
          >
            <h1 className="font-display">Get In Touch</h1>
            <p>
              Ready to transform your space? We'd love to hear from you. 
              Contact us today for a consultation and let's bring your vision to life.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-main-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="contact-form-section"
            >
              <div className="contact-form-card">
                <h2>Send us a Message</h2>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <div className="input-with-icon">
                        <User size={20} />
                        <input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="input-with-icon">
                        <Phone size={20} />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <div className="input-with-icon">
                        <Mail size={20} />
                        <input
                          type="email"
                          name="email"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <textarea
                      name="message"
                      placeholder="Tell us about your project..."
                      rows="5"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary">
                    <Send size={20} />
                    Send Message
                  </button>
                </form>

                <div className="contact-divider">
                  <span>or</span>
                </div>

                <a 
                  href={whatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                >
                  <MessageSquare size={20} />
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="contact-info-section"
            >
              <div className="contact-info-cards">
                {contactInfo.map((info, index) => (
                  <div key={index} className="contact-info-card">
                    <div className="contact-info-icon">
                      {info.icon}
                    </div>
                    <div className="contact-info-content">
                      <h3>{info.title}</h3>
                      {info.details.map((detail, idx) => (
                        <p key={idx}>{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section map-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="map-container"
          >
            <h2 className="map-title">Find Us Here</h2>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3934.335721597581!2d76.75049777486552!3d9.5663007905181!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b0635a66b9ef98f%3A0xa4cf2631d723dcf0!2sJMJ%20Furniture%20%26%20Interiors!5e0!3m2!1sen!2sin!4v1701795354135!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: 'var(--border-radius)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="JMJ Furniture & Interiors Location"
            ></iframe>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;